import { ActionIcon, Flex, Group, Modal, SegmentedControl, Stack, Text } from "@mantine/core";
import {
  IChecklistItemPopulated,
  IChecklistItemRow,
  IChecklistPopulated,
  IStampSettings,
} from "@mctypes/checklist.types";
import { IHistoryLogDepricated, Paths, PathValue } from "@mctypes/global.types";
import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../hooks";

import { IconTrash } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import HistoryLogsDepricated from "../../../../components/HistoryLogsDepricated";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { doGetChecklistRowLogs } from "../checklistAPI";
import {
  deleteChecklistCategoryThunk,
  deleteChecklistItemThunk,
  updateChecklistCategoryThunk,
} from "../checklistThunk";
import ChecklistDetailsLegacy from "./ChecklistDetails/ChecklistDetailsLegacy";

const ChecklistModalLegacy: React.FC<{
  checklistRow: IChecklistItemRow;
  editable: boolean;
  columns: ColDef[];
  stampSettings: IStampSettings;
  closeIssueItemModal: () => void;
  checklistOverride?: IChecklistPopulated | null;
  onDeleteItem?: (itemId: string) => void | Promise<void>;
  onDeleteCategory?: (categoryId: string) => void | Promise<void>;
  onUpdateCategoryAttribute?: (
    categoryId: string,
    parameter: string,
    oldValue: string,
    newValue: IChecklistItemRow[keyof IChecklistItemRow],
  ) => void | Promise<void>;
  updateRowAttribute: <P extends Paths<IChecklistItemPopulated, 4>>(
    rowId: string,
    path: P,
    oldValue: string,
    newValue: PathValue<IChecklistItemPopulated, P>,
  ) => void;
}> = ({
  checklistRow,
  editable,
  columns,
  closeIssueItemModal,
  updateRowAttribute,
  stampSettings,
  checklistOverride,
  onDeleteItem,
  onDeleteCategory,
  onUpdateCategoryAttribute,
}) => {
  const [selectedTab, setSelectedTab] = useState<string>("info");
  const [isItem, setIsRow] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.adminReducer.project);
  const checklistFromRedux = useAppSelector((state) => state.checklistReducer.checklist);
  const checklist = checklistOverride ?? checklistFromRedux;
  const isMcTemplate = project?.maconomy?.projectNo === 100;

  const removeItem = async () => {
    if (onDeleteItem != null) {
      await onDeleteItem(checklistRow._id);
    } else {
      dispatch(deleteChecklistItemThunk(checklistRow._id));
    }
    closeIssueItemModal();
  };

  const removeCategory = async () => {
    if (onDeleteCategory != null) {
      await onDeleteCategory(checklistRow._id);
    } else {
      dispatch(deleteChecklistCategoryThunk(checklistRow._id));
    }
    closeIssueItemModal();
  };

  const getLogs = async (projectId: string, itemId: string): Promise<IHistoryLogDepricated[]> => {
    return await doGetChecklistRowLogs(projectId, checklistRow.originalCategory.checklist.toString(), itemId);
  };

  const onAssignedToChange = async <T extends keyof IChecklistItemRow>(
    parameter: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ) => {
    if (onUpdateCategoryAttribute != null) {
      await onUpdateCategoryAttribute(checklistRow._id, parameter, oldValue, newValue);
      return;
    }

    dispatch(updateChecklistCategoryThunk(checklistRow._id, parameter, newValue, oldValue));
    dispatch(setShowSuccessMessageThunk("Sjekklistekategori oppdatert"));
  };
  useEffect(() => {
    if (checklistRow.originalItem != null) {
      setIsRow(true);
    } else if (checklistRow.originalCategory != null) {
      setIsRow(false);
    }
  }, [checklistRow]);

  if (checklist == null) {
    return null;
  }

  return (
    <Modal
      opened
      onClose={closeIssueItemModal}
      size="xl"
      withinPortal={true}
      title={
        <Text lineClamp={1} fz="md">
          {isItem === true
            ? `${checklistRow.rowId}: ${checklistRow.title}`
            : `${checklistRow.rowId}: ${checklistRow.originalCategory.title}`}
        </Text>
      }
    >
      <Stack gap="md">
        <Flex align="center" gap="md">
          <SegmentedControl
            color="mc-primary"
            value={selectedTab}
            onChange={setSelectedTab}
            data={[
              { label: "Info", value: "info" },
              { label: "Logg", value: "logs" },
            ]}
            fullWidth
            style={{ maxWidth: "14rem", width: "100%" }}
          />

          <Group justify="flex-end" style={{ flex: 1 }}>
            {editable && isItem === true && (
              <ConfirmDialog confirmCb={removeItem} message={`Vil du virkelig slette rad??`} title={`Slett rad`}>
                <ActionIcon color="red" variant="light" aria-label="Slett rad">
                  <IconTrash />
                </ActionIcon>
              </ConfirmDialog>
            )}
            {editable && isItem === false && (
              <ConfirmDialog
                confirmCb={removeCategory}
                message={`Vil du virkelig slette kategori og alle rader?`}
                title={`Fjern kategori`}
              >
                <ActionIcon color="red" variant="light" aria-label="Fjern kategori">
                  <IconTrash />
                </ActionIcon>
              </ConfirmDialog>
            )}
          </Group>
        </Flex>

        <Stack gap="md">
          {selectedTab === "info" && (
            <ChecklistDetailsLegacy
              updateRowAttribute={updateRowAttribute}
              onAssignedToChange={onAssignedToChange}
              checklistRow={checklistRow}
              isItem={isItem}
              isTemplate={checklist.isTemplate}
              checkTypes={checklist.checkTypes}
              editable={editable}
              stampSettings={stampSettings}
            />
          )}
          {selectedTab === "logs" && (
            <HistoryLogsDepricated
              projectId={isMcTemplate === true ? project._id : checklistRow.originalCategory.project.toString()}
              itemId={checklistRow._id}
              modelColumns={columns}
              getLogs={getLogs}
            />
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ChecklistModalLegacy;
